import moment from 'moment';

export const formatSaleDate = (createdate) => {
  if (!createdate) return 'N/A';
  return moment(createdate, 'x').format('DD/MM/YY HH:mm:ss');
};

export const formatSaleChannel = (channel, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  const channelMap = {
    w: t('sales.channel_website', { _: 'Website' }),
    m: t('sales.channel_mobile', { _: 'Mobile' }),
    sp: t('sales.channel_sale_point', { _: 'Sale Point' }),
    sa: t('sales.channel_sale_agent', { _: 'Sale Agent' }),
    op: t('sales.channel_org_promoter', { _: 'Org C. Promoter' }),
    vp: t('sales.channel_venue_promoter', { _: 'Venue Promoter' }),
    a: t('sales.channel_affiliate', { _: 'Affiliate' }),
    ocm: t('sales.channel_org_manager', { _: 'Org C. Manager' }),
    vm: t('sales.channel_venue_manager', { _: 'Venue Manager' }),
  };
  return channelMap[channel] || 'N/A';
};

export const getSaleChannelOptions = (translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  return [
    { value: 'w', label: t('sales.channel_website', { _: 'Website' }) },
    { value: 'm', label: t('sales.channel_mobile', { _: 'Mobile' }) },
    { value: 'sp', label: t('sales.channel_sale_point', { _: 'Sale Point' }) },
    { value: 'sa', label: t('sales.channel_sale_agent', { _: 'Sale Agent' }) },
    {
      value: 'op',
      label: t('sales.channel_org_promoter', { _: 'Org C. Promoter' }),
    },
    {
      value: 'vp',
      label: t('sales.channel_venue_promoter', { _: 'Venue Promoter' }),
    },
    { value: 'a', label: t('sales.channel_affiliate', { _: 'Affiliate' }) },
    {
      value: 'ocm',
      label: t('sales.channel_org_manager', { _: 'Org C. Manager' }),
    },
    {
      value: 'vm',
      label: t('sales.channel_venue_manager', { _: 'Venue Manager' }),
    },
  ];
};

export const formatSaleStatus = (status, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  const statusMap = {
    0: t('sales.status_awaiting', { _: 'Awaiting' }),
    1: t('sales.status_confirmed', { _: 'Confirmed' }),
    2: t('sales.status_failed', { _: 'Failed' }),
    3: t('sales.status_refunded', { _: 'Refunded' }),
  };
  return statusMap[status] || t('sales.status_awaiting', { _: 'Awaiting' });
};

export const getSaleStatusOptions = (translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  return [
    { value: '0', label: t('sales.status_awaiting', { _: 'Awaiting' }) },
    { value: '1', label: t('sales.status_confirmed', { _: 'Confirmed' }) },
    { value: '2', label: t('sales.status_failed', { _: 'Failed' }) },
    { value: '3', label: t('sales.status_refunded', { _: 'Refunded' }) },
  ];
};

export const getSaleStatusColor = (status) => {
  switch (status) {
    case 1:
      return 'green';
    case 2:
      return 'red';
    case 3:
      return '#6a1b9a';
    default:
      return 'orange';
  }
};

export const formatPaymentType = (paymentType, paymentTotal, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  if (!paymentType) return 'N/A';

  if (paymentType === 'cc') {
    return paymentTotal && parseInt(paymentTotal) > 0
      ? t('sales.payment_credit_card', { _: 'Credit Card' })
      : 'N/A';
  }
  return t('sales.payment_cash', { _: 'Cash' });
};

export const formatUserFullName = (user) => {
  if (!user) return 'N/A';
  return `${user.name || ''} ${user.surname || ''}`.trim() || 'N/A';
};

export const formatUserMobile = (user) => {
  if (!user) return 'N/A';
  return user.mobile || '-';
};

/** Satış kaydından ödeme id'si (string, { id }, extras.payment). */
export const resolveSalePaymentId = (sale) => {
  if (!sale) return null;
  const candidates = [sale.payment, sale.extras?.payment, sale.paymentId];
  for (const payment of candidates) {
    if (typeof payment === 'string' && payment.trim()) return payment.trim();
    if (payment && typeof payment === 'object' && payment.id) {
      return String(payment.id);
    }
  }
  return null;
};

/** Satış satırı → ödeme detay URL (yoksa satış show). */
export const getSaleDetailHref = (saleOrId, paymentId) => {
  const pay = paymentId || resolveSalePaymentId(saleOrId);
  if (pay) return `/#/payments/${encodeURIComponent(pay)}/show`;
  const id = typeof saleOrId === 'string' ? saleOrId : saleOrId?.id;
  if (id) return `/#/sales/${encodeURIComponent(id)}/show`;
  return '/#/sales';
};

export const formatEventName = (event) => {
  if (!event) return 'N/A';
  return event.name || 'N/A';
};

export const formatOrderId = (oid) => {
  return oid || '-';
};

export const formatProductsCount = (sale, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  if (sale.seats && sale.seats.length > 0) {
    return sale.seats.length;
  } else if (sale.tickets && sale.tickets.length > 0) {
    return t('sales.tickets_count', {
      count: sale.tickets.length,
      _: `${sale.tickets.length} tickets`,
    });
  }
  return '-';
};

export const formatProductInfo = (sale) => {
  if (sale.seats && sale.seats.length > 0) {
    const seats = sale.seats.map((seat) => seat.id);
    return seats.join(', ');
  } else if (sale.product && sale.product.title && sale.product.title.en) {
    return sale.product.title.en;
  }
  return 'N/A';
};

export const formatCouponCode = (coupon) => {
  if (!coupon) return '-';
  return coupon.code || '-';
};

/** label.title { tr, en } veya string -> görünür metin. */
export const getProductTitle = (label) => {
  if (!label) return '';
  const title = label.title ?? label;
  if (typeof title === 'string') return title;
  if (title && typeof title === 'object') {
    return title.tr || title.en || '';
  }
  return '';
};

/**
 * sale.details ({ [groupKey]: { [productId]: { count, label } } }) yapısını
 * iade için düz bir ürün listesine çevirir.
 */
export const getSaleProducts = (sale) => {
  const details = sale?.details;
  if (!details || typeof details !== 'object') return [];

  const products = [];
  Object.entries(details).forEach(([groupKey, group]) => {
    if (!group || typeof group !== 'object') return;
    Object.entries(group).forEach(([productId, line]) => {
      if (!line || typeof line !== 'object') return;
      const count = Number(line.count ?? 0);
      if (count <= 0) return;
      const label = line.label ?? {};
      products.push({
        groupKey,
        productId,
        count,
        price: Number(label.price ?? 0),
        title: getProductTitle(label) || productId,
        type: label.type ?? groupKey,
      });
    });
  });
  return products;
};

export const checkSalesPermissions = (permissions) => {
  let isManager = false;
  let isOperator = false;
  let isEditor = false;
  let canView = false;

  if (permissions?.crole) {
    if (permissions.crole === 'organisationCompanyManager' && permissions.rid) {
      isManager = true;
      canView = true;
    } else if (permissions.crole === 'venueManager' && permissions.rid) {
      isManager = true;
      canView = true;
    } else if (permissions.crole === 'operator') {
      isOperator = true;
    } else if (permissions.crole === 'editor') {
      isEditor = true;
    } else if (permissions.crole === 'admin') {
      canView = true;
    }
  }

  return {
    isManager,
    isOperator,
    isEditor,
    canView,
  };
};

export const getSalesPageTitle = (eventID, stockID, userID, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  if (eventID && stockID) {
    return {
      title: t('sales.title_session', { _: 'Event Session / Sales List' }),
      description: t('sales.desc_session', {
        _: 'This list shows all sales for the selected event session.',
      }),
    };
  } else if (eventID) {
    return {
      title: t('sales.title_event', { _: 'Event / Sales List' }),
      description: t('sales.desc_event', {
        _: 'This list shows all sales for the selected event.',
      }),
    };
  } else if (userID) {
    return {
      title: t('sales.title_user', { _: 'User / Sales List' }),
      description: t('sales.desc_user', { _: 'This list shows all sales for the selected user.' }),
    };
  } else {
    return {
      title: t('sales.title_default', { _: 'Sales List' }),
      description: t('sales.desc_default', {
        _: 'This list shows the last 100 entries ordered on date/time, in order to view more and specific sales you can query them via events page.',
      }),
    };
  }
};

export const getManagerColumns = (columns) => {
  return columns.filter(
    (column) =>
      column.field !== 'soldBy' && column.field !== 'channel' && column.field !== 'deleteButton',
  );
};
